import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { isBinderType } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import { leaveOrg } from '../../api/client';
import { FamilySafeList } from './FamilySafeList';
import { MemberRosterCard } from './MemberRosterCard';
import { MyFamilyNameCard } from './MyFamilyNameCard';

export function OverviewTab() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { orgs, items, refreshOrgs } = useSafe();
  // Bumped by MyFamilyNameCard's onSaved so MemberRosterCard re-fetches and
  // shows a just-saved/cleared name without a page reload — see the prop's
  // doc comment. refreshOrgs() alone does not cause this (no key-version
  // bump for an already-cached key), and calling it more often is expensive.
  const [namesRefresh, setNamesRefresh] = useState(0);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const org = orgs.find((o) => o.id === orgId);
  if (!org) return null;
  const isAdmin = org.role === 'admin';

  async function handleLeave() {
    if (!orgId) return;
    setLeaving(true);
    setLeaveError(null);
    try {
      const result = await leaveOrg(orgId);
      if (result.ok) {
        await refreshOrgs();
        navigate('/orgs');
      } else {
        setLeaveError(result.error || 'Could not leave the family. Please try again.');
        setLeaving(false);
      }
    } catch {
      setLeaveError('Could not leave the family. Please try again.');
      setLeaving(false);
    }
  }

  const sharedItems = items.filter((i) => i.orgId === orgId);
  // The Family Sites pane is login-style items only — Binder types (documents,
  // schedules, and contacts) live in the family Binder, not the Safe.
  const sites = sharedItems.filter((i) => !isBinderType(i.type));
  const contacts = sharedItems.filter((i) => i.type === 'contact');

  return (
    <div className="space-y-6">
      <MyFamilyNameCard orgId={orgId!} onSaved={() => setNamesRefresh((v) => v + 1)} />
      <FamilySafeList
        title="Family Sites"
        items={sites}
        emptyMessage="No shared sites yet. Share a login with the family from any personal item."
      />
      <FamilySafeList
        title="Family Contacts"
        items={contacts}
        emptyMessage="No shared contacts yet. Share a contact with the family from any personal contact."
      />
      <MemberRosterCard orgId={orgId!} refreshSignal={namesRefresh} />
      {isAdmin && (
        <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-4">
          <h2 className="text-xs uppercase tracking-wide text-hbs-text-mute mb-2">
            Recent activity — last 14 days
          </h2>
          <p className="text-sm text-hbs-text-mute">
            Detailed activity is on the{' '}
            <Link to={`/orgs/${orgId}/usage`} className="text-hbs-primary hover:underline">usage page</Link>.
          </p>
        </section>
      )}

      {/* Members (not admins) can leave the family. Admins can't leave — they
          transfer admin or delete the family from Manage — so this is hidden
          for them (the server also rejects an admin self-leave). */}
      {!isAdmin && (
        <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-4">
          <h2 className="text-xs uppercase tracking-wide text-hbs-text-mute mb-2">
            Leave family
          </h2>
          <p className="text-sm text-hbs-text-mute mb-3">
            You'll lose access to everything shared in {org.name}. You can be invited back later.
          </p>
          {leaveError && (
            <p className="text-sm text-hbs-danger mb-3" role="alert">{leaveError}</p>
          )}
          {confirmingLeave ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                id="confirm-leave-family-button"
                onClick={handleLeave}
                disabled={leaving}
                className="text-sm font-semibold text-white bg-hbs-danger rounded-hbs-md px-3 py-1.5 disabled:opacity-60"
                data-testid="overview.confirmLeave"
              >
                {leaving ? 'Leaving…' : `Leave ${org.name}`}
              </button>
              <button
                type="button"
                id="cancel-leave-family-button"
                onClick={() => setConfirmingLeave(false)}
                disabled={leaving}
                className="text-sm text-hbs-text-mute hover:underline"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              id="leave-family-button"
              onClick={() => setConfirmingLeave(true)}
              className="text-sm font-semibold text-hbs-danger border border-hbs-danger rounded-hbs-md px-3 py-1.5 hover:bg-hbs-danger/10"
              data-testid="overview.leaveFamily"
            >
              Leave family
            </button>
          )}
        </section>
      )}
    </div>
  );
}
